'use client';

import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, PlusIcon, Cog6ToothIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useProfile } from '@/contexts/ProfileContext';
import ProfileManagerModal from './ProfileManagerModal';
import { Profile } from '@/app/api/profiles';

interface Props {
  onProfileSwitched?: (profile: Profile) => void;
  onTriggerOnboarding?: () => void;
  /** 'nav' = compact pill for desktop nav, 'mobile' = full-width block for mobile menu */
  variant?: 'nav' | 'mobile';
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function ProfileSwitcher({
  onProfileSwitched,
  onTriggerOnboarding,
  variant = 'nav',
}: Props) {
  const { profiles, activeProfile, setActiveProfile, loading } = useProfile();
  const [managerOpen, setManagerOpen] = useState(false);

  const handleSwitch = (profile: Profile, close?: () => void) => {
    setActiveProfile(profile);
    onProfileSwitched?.(profile);
    close?.();
  };

  const handleProfileCreatedAndSwitched = (profile: Profile) => {
    onProfileSwitched?.(profile);
  };

  if (loading || !activeProfile) return null;

  // ─── Mobile variant: full-width section ──────────────────────────────────
  if (variant === 'mobile') {
    return (
      <>
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Active Profile
          </p>
          <div className="space-y-1">
            {profiles.map((profile) => {
              const isActive = activeProfile.id === profile.id;
              return (
                <button
                  key={profile.id}
                  onClick={() => handleSwitch(profile)}
                  className={classNames(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <span className="text-lg leading-none">{profile.avatarEmoji}</span>
                  <span className="flex-1 text-sm font-medium truncate">{profile.name}</span>
                  {isActive && <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setManagerOpen(true)}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            Manage Profiles
          </button>
        </div>

        <ProfileManagerModal
          isOpen={managerOpen}
          onClose={() => setManagerOpen(false)}
          onProfileSwitched={handleProfileCreatedAndSwitched}
          onTriggerOnboarding={onTriggerOnboarding}
        />
      </>
    );
  }

  // ─── Nav variant: compact pill dropdown ──────────────────────────────────
  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-all shadow-sm max-w-[180px]">
          <span className="text-base leading-none flex-shrink-0">{activeProfile.avatarEmoji}</span>
          <span className="text-sm font-medium text-gray-700 truncate">{activeProfile.name}</span>
          <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 mt-2 w-64 origin-top-left rounded-2xl bg-white shadow-xl ring-1 ring-black/5 focus:outline-none z-50 overflow-hidden">
            {/* Profile list */}
            <div className="p-2">
              <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Switch Profile
              </p>
              {profiles.map((profile) => {
                const isActive = activeProfile.id === profile.id;
                return (
                  <Menu.Item key={profile.id}>
                    {({ active, close }) => (
                      <button
                        onClick={() => handleSwitch(profile, close)}
                        className={classNames(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all',
                          isActive
                            ? 'bg-blue-50 text-blue-700'
                            : active
                            ? 'bg-gray-50 text-gray-900'
                            : 'text-gray-700'
                        )}
                      >
                        <span className="text-xl leading-none w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm flex-shrink-0">
                          {profile.avatarEmoji}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{profile.name}</p>
                          {profile.isDefault && (
                            <p className="text-xs text-gray-400">Default</p>
                          )}
                        </div>
                        {isActive && (
                          <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </div>

            {/* Divider + manage */}
            <div className="border-t border-gray-100 p-2">
              <Menu.Item>
                {({ active, close }) => (
                  <button
                    onClick={() => { close(); setManagerOpen(true); }}
                    className={classNames(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all text-sm text-gray-600',
                      active ? 'bg-gray-50' : ''
                    )}
                  >
                    <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <PlusIcon className="h-4 w-4 text-gray-500" />
                    </span>
                    <span className="font-medium">Add / Manage Profiles</span>
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      <ProfileManagerModal
        isOpen={managerOpen}
        onClose={() => setManagerOpen(false)}
        onProfileSwitched={handleProfileCreatedAndSwitched}
        onTriggerOnboarding={onTriggerOnboarding}
      />
    </>
  );
}
